import { Button, Input } from '@fsd/ui';
import { AddSquare, CloseCircle, MentionCircle } from '@solar-icons/react';
import type { ProductSpecification } from '../../types/product';

interface SpecificationsListProps {
  specifications: ProductSpecification[];
  onChange: (specifications: ProductSpecification[]) => void;
  lang: 'pt-BR' | 'en-US' | 'es-ES';
}

export function SpecificationsList({ specifications, onChange, lang }: SpecificationsListProps) {
  const addSpecification = () => {
    const newSpec: ProductSpecification = {
      id: `spec-${Date.now()}`,
      label: { 'pt-BR': '', 'en-US': '', 'es-ES': '' },
      value: { 'pt-BR': '', 'en-US': '', 'es-ES': '' },
      order: specifications.length,
    };
    onChange([...specifications, newSpec]);
  };

  const removeSpecification = (index: number) => {
    onChange(specifications.filter((_, i) => i !== index));
  };

  const updateSpecification = (index: number, field: 'label' | 'value', value: string) => {
    const newSpecs = [...specifications];
    const currentSpec = newSpecs[index];
    if (currentSpec) {
      newSpecs[index] = {
        ...currentSpec,
        [field]: {
          ...currentSpec[field],
          [lang]: value,
        },
      };
      onChange(newSpecs);
    }
  };

  return (
    <div className="space-y-3">
      <span className="text-sm font-medium text-foreground block">Especificações Técnicas</span>
      <div className="space-y-3 pt-2">
        {specifications.length === 0 ? (
          <div className="text-center py-8 bg-muted rounded-md border border-dashed border-border">
            <p className="text-sm text-muted-foreground">Nenhuma especificação adicionada</p>
          </div>
        ) : (
          specifications.map((spec, index) => (
            <div
              key={spec.id}
              className="p-4 bg-muted/50 rounded-md border border-border space-y-3"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MentionCircle size={16} />
                  <span>#{index + 1}</span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeSpecification(index)}
                >
                  <CloseCircle size={16} />
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-xs text-muted-foreground mb-1 block">
                    Nome da Especificação (
                    {lang === 'pt-BR' ? 'PT' : lang === 'en-US' ? 'EN' : 'ES'})
                  </span>
                  <Input
                    value={spec.label[lang]}
                    onChange={(e) => updateSpecification(index, 'label', e.target.value)}
                    placeholder={
                      lang === 'pt-BR'
                        ? 'Ex: Bateria'
                        : lang === 'en-US'
                          ? 'Ex: Battery'
                          : 'Ex: Batería'
                    }
                  />
                </div>
                <div>
                  <span className="text-xs text-muted-foreground mb-1 block">
                    Valor ({lang === 'pt-BR' ? 'PT' : lang === 'en-US' ? 'EN' : 'ES'})
                  </span>
                  <Input
                    value={spec.value[lang]}
                    onChange={(e) => updateSpecification(index, 'value', e.target.value)}
                    placeholder={
                      lang === 'pt-BR'
                        ? 'Ex: 5000mAh'
                        : lang === 'en-US'
                          ? 'Ex: 5000mAh'
                          : 'Ex: 5000mAh'
                    }
                  />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <Button type="button" variant="outline" size="sm" onClick={addSpecification}>
        <AddSquare size={16} className="mr-2" />
        Adicionar Especificação
      </Button>
    </div>
  );
}
